/**
 * Shared password-policy helpers used anywhere the panel creates or updates credentials.
 * The regex is the source of truth for the minimum platform password contract.
 */
export type PasswordStrength = 'weak' | 'fair' | 'strong';

export interface PasswordPolicyChecks {
  minLength: boolean;
  uppercase: boolean;
  lowercase: boolean;
  digit: boolean;
  specialCharacter: boolean;
}

/**
 * Leading/trailing whitespace should never help a password satisfy policy, and spaces do not
 * count as special characters. The special-character check therefore excludes all whitespace.
 */
export const PASSWORD_POLICY_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9\s]).{8,}$/;

export function getPasswordPolicyChecks(value: string): PasswordPolicyChecks {
  const normalizedValue = value.trim();

  return {
    minLength: normalizedValue.length >= 8,
    uppercase: /[A-Z]/.test(normalizedValue),
    lowercase: /[a-z]/.test(normalizedValue),
    digit: /\d/.test(normalizedValue),
    specialCharacter: /[^A-Za-z0-9\s]/.test(normalizedValue)
  };
}

export function isPasswordPolicyValid(value: string): boolean {
  return PASSWORD_POLICY_REGEX.test(value.trim());
}

export function getPasswordStrength(value: string): PasswordStrength {
  const checks = getPasswordPolicyChecks(value);
  const passedChecks = Object.values(checks).filter(Boolean).length;

  if (passedChecks === Object.keys(checks).length) {
    return 'strong';
  }

  if (passedChecks >= 3) {
    return 'fair';
  }

  return 'weak';
}
